import { Matrix, Mesh, Observer, PointerInfo, Quaternion, Tags, Vector3, VertexData } from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs';
import { BaseSystem, SystemOperationDefinition, Zed } from '@modules/core';
import { throwOnFrameTimeout } from '@modules/core/src/utils/FrameTimeout';
import { createEntityFactory } from '@modules/standard';
import { Entity as LODEntity } from '@modules/standard/src/ecs/system/LODSystem';
import { World } from 'miniplex';

import { DanjiHoData, UnitDetailData } from '../..';
import { Base64, floatArrayToVector3 } from '../../utiles';
import { Edge, KMeansCluster, KMeansClusterInstance } from '../../utiles/KMeansCluster';
import { Entity as BaseEntity } from '../Entity';

import { DongEntity } from './DongSystem';
import { ScreenDistanceEntity } from './ScreenDistanceSystem';
import { UnitPanelEntity } from './UnitPanelSystem';


export type UnitEntity = {
  VertexData?: VertexData;
  /** Unit 초기화, 1 frame 유지 */
  UnitInit?: true;
  /**
   * 세대 데이터
   */
  UnitData?: DanjiHoData;
  /**
   * 세대 상세 데이터
   */
  UnitDetail?: Promise<UnitDetailData> | UnitDetailData;
  /**
   * 세대 패널 위치 계산을 위한 k-means 클러스터
   */
  UnitCluster?: KMeansClusterInstance;
};

type Entity = BaseEntity & LODEntity & DongEntity & ScreenDistanceEntity & UnitPanelEntity & UnitEntity;

export class UnitSystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('UnitSystem');

  private readonly edgeOffset = 0.1;

  private readonly world;
  private readonly scene;
  private readonly createEntity;
  private readonly withDongInit;
  private readonly withUnitInit;
  private readonly withUnitData;
  private readonly withoutUnitPanels;
  private readonly withoutVertexData;
  private readonly withoutUnitDataMesh;
  private readonly withUnitDataMesh;
  private readonly withoutUnitCluster;
  private readonly withUnitPanels;

  private readonly options = {
    /**
     * LOD를 사용하여 화면에 보이는 세대 모델부터 클러스터 생성
     */
    useLod: true,
  };
  private onSelectedUnitPanelObserver: Observer<PointerInfo> | undefined = undefined;

  constructor({
    [Zed.WORLD_TOKEN]: world,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
  }: {
    [Zed.WORLD_TOKEN]: World<Entity>;
    [BabylonjsModule.INJECTABLE_TOKEN]: BabylonjsModule;
  }) {
    this.world = world;
    this.scene = babylonjs.scene;
    this.createEntity = createEntityFactory(world);
    this.withDongInit = world.with('DongInit', 'DongData')
    this.withUnitInit = world.with('UnitInit');
    this.withUnitData = world.with('UnitData', 'parent');
    this.withoutUnitPanels = this.withUnitData.without('UnitPanels');
    this.withoutVertexData = this.withoutUnitPanels.without('VertexData');
    this.withoutUnitDataMesh = this.withoutUnitPanels.with('VertexData').without('TransformNode');
    this.withUnitDataMesh = this.withoutUnitPanels.with('TransformNode');
    this.withoutUnitCluster = this.withUnitDataMesh.with('VertexData', 'ScreenDistance').without('UnitCluster');
    this.withUnitPanels = this.world.with('UnitPanels', 'TransformNode');
  }

  private updateUnitCluster = () => {
    for (const entity of this.withoutUnitCluster.entities.toSorted(
      (a, b) => a.ScreenDistance.distanceSquared - b.ScreenDistance.distanceSquared
    )) {
      throwOnFrameTimeout();
      const { VertexData } = entity;
      const mesh = entity.TransformNode as Mesh;

      if (mesh._internalAbstractMeshDataInfo._currentLOD !== mesh) {
        continue;
      }

      if (!mesh.isVisible) {
        continue;
      }
      if (mesh.isOccluded) {
        continue;
      }
      const cluster = this.calculateCluster(entity, VertexData, mesh);
      this.world.addComponent(entity, 'UnitCluster', cluster);
    }
  };

  private updateUnitMesh = () => {
    for (const entity of this.withoutUnitDataMesh) {
      throwOnFrameTimeout();
      const { VertexData } = entity;
      const mesh = this.createUnitMesh(entity, VertexData);

      mesh.isPickable = false;

      const { min, max } = mesh.getHierarchyBoundingVectors();
      const worldPosition = min.add(max).scale(0.5);

      this.world.update(entity, {
        TransformNode: mesh,
        ScreenDistanceOptions: {
          maxDistanceSquared: 250 ** 2,
          position: worldPosition,
        },
        ...(this.options.useLod && {
          LODDefinition: {
            LODs: [
              {
                distanceOrScreenCoverage: 250,
                mesh: null,
              },
            ],
          },
        }),
      });
    }
  };

  private updateUnitVertexData = () => {
    for (const entity of this.withoutVertexData) {
      throwOnFrameTimeout();
      const { UnitData } = entity;
      const vd = this.computeVertexData(UnitData);
      this.world.addComponent(entity, 'VertexData', vd);
    }
  };

  private createUnitInit = () => {
    for (const entity of this.withDongInit) {
      entity.DongData.building.hos.forEach((ho) => this.createEntity({ parent: entity, UnitInit: true, UnitData: ho }));
    }
  };

  // NOTE: generateDongs 완료 후 렌더링 루프가 돌기 전에 유닛을 미리 생성하는 용도로 추가
  // 동 초기화가 완료되면 DongInit 컴포넌트가 삭제되므로 필요할 경우 그 이전에 실행되어야 함.
  public createUnitImmediately(unitData: DanjiHoData) {
    const buildingEntity = this.withDongInit.entities.find((entity) => entity.DongData.name === unitData.meta.동);
    // buildingEntity가 없다면 UnitData까지 생성된 상태이므로 처리하지 않는다.
    if (buildingEntity) {
      const unitEntity = this.createEntity({ parent: buildingEntity, UnitInit: true, UnitData: unitData });
      const vd = this.computeVertexData(unitData);
      this.world.addComponent(unitEntity, 'VertexData', vd);
  
      const { parent, UnitData } = unitEntity;
  
      const mesh = new Mesh(`${UnitData.meta.호}`);
      mesh.parent = parent.TransformNode ?? null;
      mesh.visibility = 0;
      mesh.occlusionType = Mesh.OCCLUSION_TYPE_OPTIMISTIC;
      mesh.cullingStrategy = Mesh.CULLINGSTRATEGY_BOUNDINGSPHERE_ONLY;
  
      vd.applyToMesh(mesh);
  
      Tags.AddTagsTo(mesh, 'unit');
  
      mesh.isPickable = false;
  
      const { min, max } = mesh.getHierarchyBoundingVectors();
      const worldPosition = min.add(max).scale(0.5);
  
      this.world.update(unitEntity, {
        TransformNode: mesh,
        ScreenDistanceOptions: {
          maxDistanceSquared: 250 ** 2,
          position: worldPosition,
        },
        ...(this.options.useLod && {
          LODDefinition: {
            LODs: [
              {
                distanceOrScreenCoverage: 250,
                mesh: null,
              },
            ],
          },
        }),
      });
    }
  }

  private cleanupUnitInit = () => {
    for (const entity of this.withUnitInit) {
      this.world.removeComponent(entity, 'UnitInit');
    }
  };

  private cleanupTempData = () => {
    for (const entity of this.withUnitPanels) {
      entity.TransformNode.dispose();
      this.world.removeComponent(entity, 'TransformNode');
      this.world.removeComponent(entity, 'VertexData');
    }
  };

  private computeVertexData = (UnitData: DanjiHoData) => {
    const vd = new VertexData();
    vd.positions = Base64.decodeXZY(UnitData.coordinatesBase64);
    vd.indices = Array.from({ length: vd.positions.length / 3 }, (_, i) => i).reverse(); // reverse indices to flip face according to handedness
    vd.normals = [];
    VertexData.ComputeNormals(vd.positions, vd.indices, vd.normals);
    return vd;
  };

  private createUnitMesh = (entity: (typeof this.withoutUnitDataMesh.entities)[number], vd: VertexData) => {
    const { parent, UnitData } = entity;

    const mesh = new Mesh(`${UnitData.meta.호}`);
    mesh.parent = parent.TransformNode ?? null;
    mesh.visibility = 0;
    mesh.occlusionType = Mesh.OCCLUSION_TYPE_OPTIMISTIC;
    mesh.cullingStrategy = Mesh.CULLINGSTRATEGY_BOUNDINGSPHERE_ONLY;

    vd.applyToMesh(mesh);

    Tags.AddTagsTo(mesh, 'unit');

    return mesh;
  };

  private generateCluster = (
    entity: (typeof this.withUnitData.entities)[number],
    positions: Vector3[],
    normals: Vector3[]
  ) => {
    let dirs: Vector3[];
    let rot = Matrix.Identity();

    const angle = entity.UnitData.meta.향;
    Matrix.FromQuaternionToRef(Quaternion.FromEulerAngles(0, angle, 0), rot);
    dirs = [
      Vector3.TransformCoordinates(Vector3.LeftReadOnly, rot),
      Vector3.TransformCoordinates(Vector3.RightReadOnly, rot),
      Vector3.TransformCoordinates(Vector3.LeftHandedForwardReadOnly, rot),
      Vector3.TransformCoordinates(Vector3.LeftHandedBackwardReadOnly, rot),
    ];

    let bottomY = positions.reduce((acc, v) => Math.min(acc, v.y), Infinity);
    const bottomVertices: number[] = [];

    for (let i = 0; i < positions.length; i++) {
      const v = positions[i]!;
      const n = normals[i]!;
      if (n.equalsWithEpsilon(Vector3.UpReadOnly) || n.equalsWithEpsilon(Vector3.DownReadOnly)) {
        continue;
      }
      if (true /*this.type === HoRendererType.HoRenderer*/) {
        if (dirs!.every((t) => t.cosSimilarity(n) < 0.5)) {
          continue;
        }
      }
      if (v.y === bottomY) {
        bottomVertices.push(i);
      }
    }
    return new KMeansCluster(positions, normals, bottomVertices, rot);
  };

  private generateEdges = (kmeans: KMeansClusterInstance, positions: Vector3[], normals: Vector3[], height: number) => {
    const centroidCenter = new Array<Vector3>(4);
    const centroidNormals = new Array<Vector3>(4);
    const centroidInitialNormals = new Array<Vector3>(4);
    for (const [i, cluster] of kmeans.clusters.entries()) {
      centroidCenter[i] = cluster.idxs
        .reduce((a, idx) => a.addInPlace(positions[idx]!), Vector3.Zero())
        .scaleInPlace(1 / cluster.idxs.length);
      centroidNormals[i] = cluster.centroid;
      centroidInitialNormals[i] = cluster.initialCentroid;
    }

    const clusterVertices = new Array<Vector3[]>(4);
    const clusterNormals = new Array<Vector3[]>(4);
    for (const [i, cluster] of kmeans.clusters.entries()) {
      clusterVertices[i] = cluster.idxs.map((t) => positions[t]!);
      clusterNormals[i] = cluster.idxs.map((t) => normals[t]!);
    }

    kmeans.edges = new Array<Edge | undefined>(4);
    for (const [i, cluster] of kmeans.clusters.entries()) {
      kmeans.edges[i] = cluster.getEdge(positions, normals, this.edgeOffset, height);
    }
  };

  private calculateCluster(entity: (typeof this.withUnitData.entities)[number], vd: VertexData, mesh: Mesh) {
    const { UnitData } = entity;
    const positions = floatArrayToVector3(vd.positions);
    const normals = floatArrayToVector3(vd.normals);
    const cluster = new KMeansClusterInstance(
      this.generateCluster(entity, positions, normals),
      mesh.getAbsolutePosition()
    );
    this.generateEdges(cluster, positions, normals, UnitData.meta.높이 / 2);

    const center = mesh.getBoundingInfo().boundingSphere.center;
    for (const [i, edge] of cluster.edges.entries()) {
      if (!edge) continue;

      cluster.update(mesh.name, i, center);
    }

    return cluster;
  }

  dispose(): void {
    if (this.onSelectedUnitPanelObserver) this.scene.onPointerObservable.remove(this.onSelectedUnitPanelObserver);
  }

  readonly operations: SystemOperationDefinition[] = [
    {
      group: 'beforeRender',
      run: this.createUnitInit,
    },
    {
      group: 'beforeRender',
      run: this.updateUnitMesh,
    },
    {
      group: 'beforeRender',
      run: this.updateUnitVertexData,
    },
    {
      group: 'beforeRender',
      run: this.updateUnitCluster,
      after: [this.updateUnitMesh],
    },
    {
      group: 'afterRender',
      run: this.cleanupUnitInit,
    },
    {
      group: 'afterRender',
      run: this.cleanupTempData,
    },
  ];
}

/**
 * @deprecated Use UnitTypeSystem instead
 */
export type RoomTypeSystem = UnitSystem;
